import React from "react";
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { getUserProfileDetails } from "selectors/appSelector";
import logo from "assets/app/logo.png";

const useStyles = makeStyles((theme) => ({
  previewContainer: {
    position: "sticky",
    top: 0,
    height: "100vh",
    overflow: "auto",
    padding: theme.spacing(2),
    backgroundColor: "#f5f5f5",
    borderLeft: "1px solid #e0e0e0",
  },
  invoicePaper: {
    padding: theme.spacing(2),
    backgroundColor: "white",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    borderRadius: theme.spacing(0.5),
    position: "relative",
    margin: theme.spacing(1),
    height: "fit-content",
  },
  invoiceHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    borderBottom: "1px solid #e0e0e0",
  },
  leftHeader: {
    display: "flex",
    flexDirection: "column",
  },
  invoiceTitle: {
    fontSize: "2rem",
    fontWeight: "bold",
    color: "#2c3e50",
    marginBottom: theme.spacing(0.5),
    letterSpacing: "2px",
  },
  companyName: {
    fontSize: "1rem",
    fontWeight: "600",
    color: "#7f8c8d",
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  rightHeader: {
    textAlign: "right",
  },
  previewLabel: {
    backgroundColor: "#e8f4f8",
    color: "#2980b9",
    padding: "4px 12px",
    borderRadius: "4px",
    fontSize: "0.75rem",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: theme.spacing(1),
    display: "inline-block",
  },
  billFromSection: {
    marginBottom: theme.spacing(2),
    textAlign: "left",
  },
  sectionHeader: {
    fontSize: "0.875rem",
    fontWeight: "700",
    color: "#34495e",
    marginBottom: theme.spacing(1),
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  companyInfo: {
    fontSize: "0.875rem",
    color: "#7f8c8d",
    lineHeight: 1.4,
    textAlign: "left",
    "& div": {
      marginBottom: "2px",
    },
  },
  billingSection: {
    marginBottom: theme.spacing(2),
    display: "flex",
    justifyContent: "space-between",
    textAlign: "left",
  },
  billToSection: {
    flex: 1,
    marginRight: theme.spacing(4),
    textAlign: "left",
  },
  invoiceDetailsSection: {
    flex: 1,
    maxWidth: "300px",
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(0.75),
    alignItems: "center",
  },
  detailLabel: {
    fontSize: "0.875rem",
    fontWeight: "600",
    color: "#7f8c8d",
    minWidth: "100px",
  },
  detailValue: {
    fontSize: "0.875rem",
    color: "#2c3e50",
    fontWeight: "500",
  },
  billTo: {
    fontSize: "1rem",
    fontWeight: "700",
    color: "#2c3e50",
    marginBottom: theme.spacing(0.5),
  },
  recipientEmail: {
    fontSize: "0.875rem",
    color: "#7f8c8d",
  },
  tableContainer: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    border: "1px solid #e0e0e0",
    borderRadius: theme.spacing(0.5),
    overflow: "hidden",
  },
  tableHeader: {
    backgroundColor: "#34495e",
    "& .MuiTableCell-head": {
      color: "white",
      fontWeight: "600",
      fontSize: "0.875rem",
      padding: "12px 16px",
      borderBottom: "none",
    },
  },
  tableRow: {
    "&:nth-of-type(odd)": {
      backgroundColor: "#f8f9fa",
    },
    "& .MuiTableCell-body": {
      padding: "12px 16px",
      fontSize: "0.875rem",
      borderBottom: "1px solid #e0e0e0",
    },
  },
  emptyRow: {
    "& .MuiTableCell-body": {
      padding: "40px 16px",
      textAlign: "center",
      color: "#95a5a6",
      fontSize: "0.875rem",
    },
  },
  totalsSection: {
    // display: "flex",
    justifyContent: "end",
    marginTop: theme.spacing(1),
    gap: theme.spacing(2),
  },
  totalsLeft: {
    flex: 1,
    maxWidth: "50%",
  },
  totalsBox: {
    minWidth: "50%",
    with: "50%",
    placeSelf: "end",
    backgroundColor: "#f8f9fa",
    padding: theme.spacing(2),
    borderRadius: theme.spacing(0.5),
    border: "1px solid #e0e0e0",
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
    alignItems: "center",
  },
  totalLabel: {
    fontSize: "0.875rem",
    color: "#7f8c8d",
    fontWeight: "500",
  },
  totalValue: {
    fontSize: "0.875rem",
    color: "#2c3e50",
    fontWeight: "600",
  },
  grandTotalRow: {
    borderTop: "2px solid #34495e",
    paddingTop: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  grandTotalLabel: {
    fontSize: "1rem",
    color: "#2c3e50",
    fontWeight: "700",
  },
  grandTotalValue: {
    fontSize: "1.125rem",
    color: "#2c3e50",
    fontWeight: "700",
  },
  notesSection: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: "1px solid #e0e0e0",
  },
  notesTitle: {
    fontSize: "0.875rem",
    fontWeight: "700",
    color: "#34495e",
    marginBottom: theme.spacing(1),
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  notesContent: {
    fontSize: "0.875rem",
    color: "#7f8c8d",
    lineHeight: 1.5,
  },
}));

const PreviewInvoice = ({ formData, invoiceItems = [], isModal = false }) => {
  const classes = useStyles();
  const userProfile = useSelector(getUserProfileDetails);
  const { company } = userProfile;

  // Calculate totals
  const calculateItemTotal = (item) => {
    const quantity = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.price) || 0;
    const discPct = parseFloat(item.discPct) || 0;
    const taxPct = parseFloat(item.taxPct) || 0;

    const base = quantity * price; // before discount and tax
    const discount = (base * discPct) / 100;
    const beforeTax = base - discount; // taxable value
    const tax = (beforeTax * taxPct) / 100;
    const total = beforeTax + tax;

    return { base, discount, beforeTax, tax, total };
  };

  const totals = invoiceItems.reduce(
    (acc, item) => {
      const t = calculateItemTotal(item);
      acc.base += t.base;
      acc.discount += t.discount;
      acc.beforeTax += t.beforeTax;
      acc.tax += t.tax;
      acc.total += t.total;
      return acc;
    },
    { base: 0, discount: 0, beforeTax: 0, tax: 0, total: 0 }
  );

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      return format(new Date(date), "MM/dd/yyyy");
    } catch {
      return "N/A";
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const formatCityStateZip = (city, state, zip) => {
    const cityPart = city ? city : "";
    const stateZip = [state, zip].filter(Boolean).join(" ");
    return [cityPart, stateZip].filter(Boolean).join(", ");
  };

  const formatAddressLines = (address1, address2, city, state, zip) => {
    const lines = [];
    const street = [address1, address2].filter(Boolean).join(" ");
    if (street) lines.push(street);
    const csz = formatCityStateZip(city, state, zip);
    if (csz) lines.push(csz);
    return lines;
  };

  // Convert numbers to words (English, dollars only)
  const numberToWords = (num) => {
    if (num === 0) return "Zero";
    const belowTwenty = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];
    const thousands = ["", "Thousand", "Million", "Billion"]; // enough for invoices

    const wordsForHundreds = (n) => {
      let res = "";
      if (n >= 100) {
        res += belowTwenty[Math.floor(n / 100)] + " Hundred";
        n %= 100;
        if (n) res += " ";
      }
      if (n >= 20) {
        res += tens[Math.floor(n / 10)];
        n %= 10;
        if (n) res += "-" + belowTwenty[n];
      } else if (n > 0) {
        res += belowTwenty[n];
      }
      return res;
    };

    const integer = Math.floor(num);
    const cents = Math.round((num - integer) * 100);
    let i = integer;
    let k = 0;
    let parts = [];
    while (i > 0) {
      const chunk = i % 1000;
      if (chunk > 0) {
        const chunkWords = wordsForHundreds(chunk);
        const label = thousands[k];
        parts.unshift(chunkWords + (label ? " " + label : ""));
      }
      i = Math.floor(i / 1000);
      k += 1;
    }
    let result = parts.join(" ") + " Dollar" + (integer !== 1 ? "s" : "");
    if (cents > 0) {
      result += " and " + cents + " Cent" + (cents !== 1 ? "s" : "");
    }
    return result + " Only";
  };

  return (
    <div className={isModal ? "" : classes.previewContainer}>
      <Paper className={classes.invoicePaper} elevation={2}>
        {/* Invoice Header */}
        <div className={classes.invoiceHeader}>
          <div className={classes.leftHeader}>
            <Typography className={classes.invoiceTitle}>INVOICE</Typography>
            <Typography className={classes.companyName}>
              BUILDER COPILOT
            </Typography>
          </div>
          <div className={classes.rightHeader}>
            <div className={classes.previewLabel}>
              {" "}
              <img
                src={logo}
                alt="builder copilot"
                className="logo p-0"
                style={{ width: "7rem" }}
              />
            </div>
          </div>
        </div>

        {/* From (Contractor) Section */}
        <div className={classes.billFromSection}>
          <Typography className={classes.sectionHeader}>From:</Typography>
          <div className={classes.companyInfo}>
            <div>
              <strong>{company.companyName}</strong>
            </div>
            {formatAddressLines(
              company.address1,
              company.address2,
              company.city,
              company.state,
              company.zipcode
            ).map((line, idx) => (
              <div key={`from-addr-${idx}`}>{line}</div>
            ))}
            {company.mobileNumber && <div>Phone: {company.mobileNumber}</div>}
            {userProfile.email && <div>Email: {userProfile.email}</div>}
            {userProfile.userName && <div>Contact: {userProfile.userName}</div>}
          </div>
        </div>

        {/* Billing Section */}
        <div className={classes.billingSection}>
          <div className={classes.billToSection}>
            <Typography className={classes.sectionHeader}>Bill To:</Typography>
            <Typography className={classes.billTo}>
              {formData?.billTo || "Recipient Name"}
            </Typography>
            {formData?.billToEmail && (
              <Typography className={classes.recipientEmail}>
                Email: {formData.billToEmail}
              </Typography>
            )}
            {formData?.billToPhone && (
              <Typography className={classes.recipientEmail}>
                Phone: {formData.billToPhone}
              </Typography>
            )}
            <div className={classes.companyInfo}>
              {formatAddressLines(
                formData?.billToAddress1,
                formData?.billToAddress2,
                formData?.billToCity,
                formData?.billToState,
                formData?.billToZip
              ).map((line, idx) => (
                <div key={`billto-addr-${idx}`}>{line}</div>
              ))}
            </div>
          </div>

          <div className={classes.invoiceDetailsSection}>
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>
                Invoice #:
              </Typography>
              <Typography className={classes.detailValue}>
                {formData?.invoiceNumber || "—"}
              </Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>
                Invoice Date:
              </Typography>
              <Typography className={classes.detailValue}>
                {formatDate(formData?.invoiceDate)}
              </Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>
                Payment Due Date:
              </Typography>
              <Typography className={classes.detailValue}>
                {formatDate(formData?.paymentDueDate)}
              </Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>PO #:</Typography>
              <Typography className={classes.detailValue}>
                {formData?.poNumber || "—"}
              </Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Job:</Typography>
              <Typography className={classes.detailValue}>
                {formData?.jobName || "—"}
              </Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Terms:</Typography>
              <Typography className={classes.detailValue}>
                {formData?.paymentTerms || "Due on Receipt"}
              </Typography>
            </div>
          </div>
        </div>

        {/* Line Items Table */}
        <TableContainer className={classes.tableContainer}>
          <Table>
            <TableHead className={classes.tableHeader}>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell align="center">Unit</TableCell>
                <TableCell align="center">Quantity/Hours</TableCell>
                <TableCell align="center">Unit Price ($)</TableCell>
                <TableCell align="right">Taxable Amount ($)</TableCell>
                <TableCell align="center">Total Discount (%)</TableCell>
                <TableCell align="center">Tax %</TableCell>
                <TableCell align="right">Amount ($)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoiceItems.length > 0 ? (
                invoiceItems.map((item, index) => {
                  const itemTotals = calculateItemTotal(item);
                  return (
                    <TableRow
                      key={item.id || index}
                      className={classes.tableRow}
                    >
                      <TableCell>{item.itemName || "N/A"}</TableCell>
                      <TableCell align="center">{item.unit || ""}</TableCell>
                      <TableCell align="center">{item.quantity || 0}</TableCell>
                      <TableCell align="center">
                        {formatCurrency(item.price)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(itemTotals.base)}
                      </TableCell>
                      <TableCell align="center">{item.discPct || 0}%</TableCell>
                      <TableCell align="center">{item.taxPct || 0}%</TableCell>
                      <TableCell align="right">
                        {formatCurrency(itemTotals.total)}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow className={classes.emptyRow}>
                  <TableCell colSpan={8}>No items added yet</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Totals Section */}
        {invoiceItems.length > 0 && (
          <div className={classes.totalsSection}>
            {/* Left textual breakdown */}
            {/* <div className={classes.totalsLeft}>
              <div className={classes.totalRow}>
                <Typography className={classes.totalLabel}>
                  Total Taxable Value:
                </Typography>
                <Typography className={classes.totalValue}>
                  {formatCurrency(totals.beforeTax)}
                </Typography>
              </div>
              <div className={classes.totalRow}>
                <Typography className={classes.totalLabel}>
                  Total Tax Amount:
                </Typography>
                <Typography className={classes.totalValue}>
                  {formatCurrency(totals.tax)}
                </Typography>
              </div>
              <div className={classes.totalRow}>
                <Typography className={classes.totalLabel}>
                  Total Value (in figure):
                </Typography>
                <Typography className={classes.totalValue}>
                  {formatCurrency(totals.total)}
                </Typography>
              </div>
              <div className={classes.totalRow}>
                <Typography className={classes.totalLabel}>
                  Total Value (in words):
                </Typography>
                <Typography className={classes.totalValue}>
                  {numberToWords(totals.total)}
                </Typography>
              </div>
              {parseFloat(formData?.retentionPct || 0) > 0 && (
                <div className={classes.totalRow}>
                  <Typography className={classes.totalLabel}>
                    Retention ({parseFloat(formData.retentionPct)}%):
                  </Typography>
                  <Typography className={classes.totalValue}>
                    {formatCurrency(
                      (totals.total * parseFloat(formData.retentionPct)) / 100
                    )}
                  </Typography>
                </div>
              )}
              <div className={`${classes.totalRow} ${classes.grandTotalRow}`}>
                <Typography className={classes.grandTotalLabel}>
                  Amount Due:
                </Typography>
                <Typography className={classes.grandTotalValue}>
                  {formatCurrency(
                    totals.total -
                      (totals.total *
                        (parseFloat(formData?.retentionPct || 0) || 0)) /
                        100
                  )}
                </Typography>
              </div>
            </div> */}

            {/* Right aligned summary box */}
            <div className={classes.totalsBox}>
              <div className={classes.totalRow}>
                <Typography className={classes.totalLabel}>
                  Total Before Discount
                </Typography>
                <Typography className={classes.totalValue}>
                  {formatCurrency(totals.base)}
                </Typography>
              </div>
              <div className={classes.totalRow}>
                <Typography className={classes.totalLabel}>
                  Total Discount
                </Typography>
                <Typography className={classes.totalValue}>
                  {formatCurrency(totals.discount)}
                </Typography>
              </div>
              <div className={classes.totalRow}>
                <Typography className={classes.totalLabel}>
                  Total Before Tax
                </Typography>
                <Typography className={classes.totalValue}>
                  {formatCurrency(totals.beforeTax)}
                </Typography>
              </div>
              <div className={classes.totalRow}>
                <Typography className={classes.totalLabel}>
                  Total Tax
                </Typography>
                <Typography className={classes.totalValue}>
                  {formatCurrency(totals.tax)}
                </Typography>
              </div>
              <div className={`${classes.totalRow} ${classes.grandTotalRow}`}>
                <Typography className={classes.grandTotalLabel}>
                  TOTAL
                </Typography>
                <Typography className={classes.grandTotalValue}>
                  {formatCurrency(totals.total)}
                </Typography>
              </div>
              {parseFloat(formData?.retentionPct || 0) > 0 && (
                <div className={classes.totalRow}>
                  <Typography className={classes.totalLabel}>
                    Retention ({parseFloat(formData.retentionPct)}%)
                  </Typography>
                  <Typography className={classes.totalValue}>
                    {formatCurrency(
                      (totals.total * parseFloat(formData.retentionPct)) / 100
                    )}
                  </Typography>
                </div>
              )}
              <div className={`${classes.totalRow} ${classes.grandTotalRow}`}>
                <Typography className={classes.grandTotalLabel}>
                  AMOUNT DUE
                </Typography>
                <Typography className={classes.grandTotalValue}>
                  {formatCurrency(
                    totals.total -
                      (totals.total *
                        (parseFloat(formData?.retentionPct || 0) || 0)) /
                        100
                  )}
                </Typography>
              </div>
            </div>
          </div>
        )}

        {/* Notes Section */}
        {formData?.notes && (
          <div className={classes.notesSection}>
            <Typography className={classes.notesTitle}>
              Terms and Conditions
            </Typography>
            <Typography className={classes.notesContent}>
              {formData.notes}
            </Typography>
          </div>
        )}
      </Paper>
    </div>
  );
};

export default PreviewInvoice;
